import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import _ from 'lodash';
import { PanelSection } from '../SidePanel';
import {
  HoverNavItem,
  IconSpan,
  NameInput,
  StickyHeader,
} from './menu-components';
import { MapContext } from '../../contexts/MapContext';
import { ItemContext } from '../../contexts/ItemContext';
import { mapModes } from '../../constants/map';
import SectionGroupIcon from '../icons/SectionGroupIcon';
import { fitSectionGroupBounds } from '../../utils/map';
import { captureEvent } from '../../utils/analytics';
import ShowHideSelectedSectionsButton from '../map-toolbars/buttons/ShowHideSelectedSectionsButton';
import IconButton from '../basics/IconButton';
import { SHORTCUTS } from '../../constants/shortcuts';
import ChevronIcon from '../icons/ChevronIcon';
import ButtonWithShortcut from '../basics/ButtonWithShortcut';
import { mutateAndDisplayErrors } from '../../utils/toast';

const SectionGroupNavItem = ({ sectionGroup }) => {
  const { id, sections, name } = sectionGroup;
  const { farmId } = useParams();
  const {
    sectionGroups: { updateSectionGroup },
    fields: { fieldSections },
    mapState: { activeSectionGroupId, setMapMode, mapRef },
  } = useContext(MapContext);

  const { setActiveKey, clearActiveKey, activeKey } = useContext(ItemContext);
  const sectionGroupSet = useMemo(
    () => new Set(sections.map((group) => group.id)),
    [sections],
  );

  const onNavClick = () => {
    setMapMode(mapModes.SECTION_GROUP_ENGAGED, id, { sectionGroup });
    fitSectionGroupBounds(sectionGroup, fieldSections, mapRef);
    captureEvent('section-group-clicked');
  };
  return (
    <HoverNavItem
      key={id}
      onClick={onNavClick}
      isActive={activeSectionGroupId === id}
      onMouseEnter={() => setActiveKey(id)}
      onMouseLeave={clearActiveKey}
      hovered={activeKey === id}
      className="d-flex align-items-center"
    >
      <IconSpan width="11px" />
      <IconSpan className="d-flex justify-content-center">
        <SectionGroupIcon />
      </IconSpan>
      <NameInput
        update={(value) => {
          mutateAndDisplayErrors(() =>
            updateSectionGroup(id, farmId, { name: value }),
          );
        }}
        name={name}
      />
      {activeKey === id && (
        <ShowHideSelectedSectionsButton
          selectedSectionIdSet={sectionGroupSet}
          className="my-0 mx-3"
          shortenText
        />
      )}
    </HoverNavItem>
  );
};

const SectionGroupsMenu = () => {
  const {
    sectionGroups: { sectionGroups },
    mapState: { mapMode },
  } = useContext(MapContext);

  const [showSectionGroups, setShowSectionGroups] = useState(true);
  const toggleSectionGroups = () => setShowSectionGroups(!showSectionGroups);

  useEffect(() => {
    if (mapMode === mapModes.SECTION_GROUP_ENGAGED) {
      setShowSectionGroups(true);
    }
  }, [mapMode]);

  const sortedSectionGroups = useMemo(() => _.sortBy(sectionGroups, ['name']), [
    sectionGroups,
  ]);

  if (!sectionGroups.length) return null;

  return (
    <PanelSection className={showSectionGroups ? '' : 'pb-0'}>
      <StickyHeader className="d-flex justify-content-between align-items-center">
        <div className="d-flex">
          <IconSpan>
            <SectionGroupIcon />
          </IconSpan>
          Section Groups
        </div>
        <ButtonWithShortcut
          as={IconButton}
          shortcut={SHORTCUTS.SHOW_HIDE_GROUPS.key}
          color="link"
          onClick={toggleSectionGroups}
          title="Show section groups"
          className="p-0 font-small"
          size="sm"
        >
          <ChevronIcon direction={showSectionGroups ? 'up' : 'down'} />
        </ButtonWithShortcut>
      </StickyHeader>
      {showSectionGroups && (
        <ul>
          {sortedSectionGroups.map((sectionGroup) => (
            <SectionGroupNavItem
              key={sectionGroup.id}
              sectionGroup={sectionGroup}
            />
          ))}
        </ul>
      )}
    </PanelSection>
  );
};

export default SectionGroupsMenu;
